/**
 * Error Monitoring Service
 * Automatically detects critical issues and triggers maintenance mode + email alerts
 */

import { sendErrorNotification } from './errorNotificationService';
import { updateSystemStatus } from './systemStatusService';
import config from '../config/environment';

const CRITICAL_ERROR_THRESHOLD = 3; // Number of critical errors before maintenance mode
const ERROR_WINDOW_MS = 5 * 60 * 1000; // 5 minutes

// Always use live WordPress API for maintenance mode (even when testing on localhost)
const LIVE_API_ENDPOINT = 'https://www.belvoircastle.com/wp/wp-json';

// Track errors for rate limiting
let errorHistory = [];
let maintenanceModeActive = false;

/**
 * Critical error types that should trigger maintenance mode
 */
const CRITICAL_ERROR_TYPES = {
  PAYMENT_TAKEN_NO_BOOKING: 'PAYMENT_TAKEN_NO_BOOKING',
  BOOKING_CREATED_NO_PAYMENT: 'BOOKING_CREATED_NO_PAYMENT',
  API_ENDPOINT_FAILED: 'API_ENDPOINT_FAILED',
  PAYMENT_PROCESSING_FAILED: 'PAYMENT_PROCESSING_FAILED',
  VENPOS_API_ERROR: 'VENPOS_API_ERROR',
  BASKET_CREATION_FAILED: 'BASKET_CREATION_FAILED',
  PAYMENT_INITIALIZATION_FAILED: 'PAYMENT_INITIALIZATION_FAILED'
};

/**
 * Check if we should trigger maintenance mode based on error frequency
 */
const shouldTriggerMaintenanceMode = (errorType) => {
  const now = Date.now();
  
  // Filter errors within the time window
  errorHistory = errorHistory.filter(err => (now - err.timestamp) < ERROR_WINDOW_MS);
  
  // Count critical errors in the window
  const criticalErrorCount = errorHistory.filter(err => 
    Object.values(CRITICAL_ERROR_TYPES).includes(err.type)
  ).length;
  
  // Add current error to history
  errorHistory.push({
    type: errorType,
    timestamp: now
  });
  
  // Trigger maintenance mode if threshold exceeded
  if (criticalErrorCount >= CRITICAL_ERROR_THRESHOLD) {
    return true;
  }
  
  // Also trigger for specific critical errors immediately
  if (errorType === CRITICAL_ERROR_TYPES.PAYMENT_TAKEN_NO_BOOKING ||
      errorType === CRITICAL_ERROR_TYPES.BOOKING_CREATED_NO_PAYMENT) {
    return true;
  }
  
  return false;
};

/**
 * Enable maintenance mode by calling the API
 */
const enableMaintenanceMode = async (reason = 'Critical errors detected in booking system') => {
  if (maintenanceModeActive) {
    return; // Already in maintenance mode
  }
  
  try {
    console.error('🚨 [MONITORING] Triggering maintenance mode due to critical errors');
    
    // Call API to enable maintenance mode (always use live endpoint for testing)
    const apiUrl = `${LIVE_API_ENDPOINT}/custom/v1/enable-maintenance-mode`;
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      },
      cache: 'no-store',
      body: JSON.stringify({
        reason: reason,
        timestamp: new Date().toISOString()
      })
    });
    
    if (response.ok) {
      maintenanceModeActive = true;
      console.error('✅ [MONITORING] Maintenance mode enabled successfully');
      
      // Automatically update system status to show maintenance mode
      try {
        const incident = {
          title: 'Maintenance Mode Active',
          description: reason,
          timestamp: new Date().toLocaleString('en-GB', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          }),
          updates: []
        };
        
        await updateSystemStatus('booking', {
          status: 'outage',
          statusText: 'MAINTENANCE MODE',
          description: `Booking system is in maintenance mode: ${reason}. New bookings are temporarily disabled.`,
          incident,
          maintenanceMode: {
            enabled: true,
            reason: reason,
            timestamp: new Date().toISOString()
          }
        });
        console.log('✅ [MONITORING] System status updated to show maintenance mode');
      } catch (statusError) {
        console.error('❌ [MONITORING] Failed to update system status:', statusError);
        // Don't throw - maintenance mode is still enabled
      }
    } else {
      const errorText = await response.text().catch(() => 'Unknown error');
      console.error(`❌ [MONITORING] Failed to enable maintenance mode via API (${response.status}):`, errorText);
    }
  } catch (error) {
    // Handle network errors gracefully
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      console.warn('⚠️ [MONITORING] Network error enabling maintenance mode (API may be unreachable):', error.message);
    } else {
      console.error('❌ [MONITORING] Error enabling maintenance mode:', error);
    }
  }
};

/**
 * Monitor API endpoint failures
 */
export const monitorApiEndpointFailure = async (endpoint, error, context = {}) => {
  // Sanitize error message - remove any "Test:" prefixes
  const rawErrorMessage = error?.message || String(error);
  const sanitizedErrorMessage = rawErrorMessage.replace(/^Test:\s*/i, '').trim();
  
  const errorDetails = {
    errorType: CRITICAL_ERROR_TYPES.API_ENDPOINT_FAILED,
    errorMessage: `API endpoint failed: ${endpoint}. ${sanitizedErrorMessage}`,
    endpoint,
    error: sanitizedErrorMessage,
    context: {
      ...context,
      endpoint,
      errorDetails: sanitizedErrorMessage
    },
    orderId: context?.orderId,
    amount: context?.amount,
    timestamp: new Date().toISOString(),
    stackTrace: error?.stack
  };
  
  console.error('🚨 [MONITORING] API Endpoint Failure:', endpoint, error);
  
  // Send notification
  await sendErrorNotification(errorDetails);
  
  // Automatically update system status to degraded when API errors occur
  try {
    const incident = {
      title: 'API Endpoint Failure',
      description: `API endpoint failed: ${endpoint}. ${sanitizedErrorMessage}`,
      timestamp: new Date().toLocaleString('en-GB', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }),
      updates: []
    };
    
    await updateSystemStatus('booking', {
      status: 'degraded',
      statusText: 'DEGRADED',
      description: `Booking system is experiencing API endpoint failures. Endpoint: ${endpoint}`,
      incident
    });
    console.log('✅ [MONITORING] System status updated to DEGRADED due to API endpoint failure');
  } catch (statusError) {
    console.error('❌ [MONITORING] Failed to update system status:', statusError);
  }
  
  // Check if maintenance mode should be triggered
  if (shouldTriggerMaintenanceMode(CRITICAL_ERROR_TYPES.API_ENDPOINT_FAILED)) {
    await enableMaintenanceMode(`Multiple API endpoint failures detected. Endpoint: ${endpoint}`);
    
    // Send additional alert about maintenance mode
    await sendErrorNotification({
      errorType: 'MAINTENANCE_MODE_TRIGGERED',
      errorMessage: `Maintenance mode automatically enabled due to multiple API endpoint failures. Endpoint: ${endpoint}`,
      endpoint,
      context: {
        triggerReason: 'Multiple API endpoint failures',
        endpoint,
        threshold: CRITICAL_ERROR_THRESHOLD,
        timeWindow: '5 minutes'
      },
      timestamp: new Date().toISOString()
    });
  }
};

/**
 * Monitor payment taken but booking not created
 */
export const monitorPaymentTakenNoBooking = async (paymentResult, orderId, error) => {
  // Sanitize error message - remove any "Test:" prefixes
  const rawErrorMessage = error?.message || String(error);
  const sanitizedErrorMessage = rawErrorMessage.replace(/^Test:\s*/i, '').trim();
  
  const errorDetails = {
    errorType: CRITICAL_ERROR_TYPES.PAYMENT_TAKEN_NO_BOOKING,
    errorMessage: 'Payment was processed but booking creation failed',
    orderId,
    paymentToken: paymentResult?.paymentToken,
    transactionId: paymentResult?.transactionId || paymentResult?.authCode,
    authCode: paymentResult?.authCode,
    amount: paymentResult?.amount,
    error: sanitizedErrorMessage,
    timestamp: new Date().toISOString(),
    stackTrace: error?.stack
  };
  
  console.error('🚨 [MONITORING] CRITICAL: Payment taken but booking failed!', errorDetails);
  
  // Send urgent notification
  await sendErrorNotification(errorDetails);
  
  // Automatically update system status to outage
  try {
    const incident = {
      title: 'Critical Error: Payment Taken But Booking Failed',
      description: `Payment was processed successfully (Order ID: ${orderId}) but booking creation failed. This is a critical data integrity issue.`,
      timestamp: new Date().toLocaleString('en-GB', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }),
      updates: []
    };
    
    await updateSystemStatus('booking', {
      status: 'outage',
      statusText: 'CRITICAL ERROR',
      description: 'Critical error: Payment was processed but booking creation failed. System is being placed in maintenance mode.',
      incident
    });
    console.log('✅ [MONITORING] System status updated to CRITICAL ERROR');
  } catch (statusError) {
    console.error('❌ [MONITORING] Failed to update system status:', statusError);
  }
  
  // Immediately trigger maintenance mode for this critical error
  await enableMaintenanceMode('Payment was processed but booking creation failed - critical data integrity issue');
};

/**
 * Monitor booking created without payment (when payment was required)
 */
export const monitorBookingCreatedNoPayment = async (bookingData, expectedAmount) => {
  const errorDetails = {
    errorType: CRITICAL_ERROR_TYPES.BOOKING_CREATED_NO_PAYMENT,
    errorMessage: `Booking was created without payment. Expected amount: £${expectedAmount}`,
    bookingReference: bookingData?.bookingReference,
    orderId: bookingData?.orderId,
    expectedAmount,
    actualAmount: bookingData?.totalAmount || 0,
    customerDetails: bookingData?.customerDetails,
    timestamp: new Date().toISOString()
  };
  
  console.error('🚨 [MONITORING] CRITICAL: Booking created without payment!', errorDetails);
  
  // Send urgent notification
  await sendErrorNotification(errorDetails);
  
  // Automatically update system status to outage
  try {
    const incident = {
      title: 'Critical Error: Booking Created Without Payment',
      description: `Booking was created (Reference: ${bookingData?.bookingReference || 'N/A'}) without payment when payment was required. Expected amount: £${expectedAmount}`,
      timestamp: new Date().toLocaleString('en-GB', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }),
      updates: []
    };
    
    await updateSystemStatus('booking', {
      status: 'outage',
      statusText: 'CRITICAL ERROR',
      description: 'Critical error: Booking was created without payment when payment was required. System is being placed in maintenance mode.',
      incident
    });
    console.log('✅ [MONITORING] System status updated to CRITICAL ERROR');
  } catch (statusError) {
    console.error('❌ [MONITORING] Failed to update system status:', statusError);
  }
  
  // Immediately trigger maintenance mode for this critical error
  await enableMaintenanceMode('Booking was created without payment when payment was required - critical data integrity issue');
};

/**
 * Monitor Venpos API errors
 */
export const monitorVenposApiError = async (endpoint, error, context = {}) => {
  const errorDetails = {
    errorType: CRITICAL_ERROR_TYPES.VENPOS_API_ERROR,
    errorMessage: `Venpos API error: ${endpoint}. ${error?.message || String(error)}`,
    endpoint,
    error: error?.message || String(error),
    context: {
      ...context,
      endpoint,
      apiBase: 'Venpos Journey API',
      errorDetails: error?.message || String(error)
    },
    orderId: context?.orderId || context?.basketId,
    timestamp: new Date().toISOString(),
    stackTrace: error?.stack
  };
  
  console.error('🚨 [MONITORING] Venpos API Error:', endpoint, error);
  
  // Send notification
  await sendErrorNotification(errorDetails);
  
  // Automatically update system status to degraded when Venpos API errors occur
  try {
    const incident = {
      title: 'Venpos API Error',
      description: `Venpos API error: ${endpoint}. ${error?.message || String(error)}`,
      timestamp: new Date().toLocaleString('en-GB', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }),
      updates: []
    };
    
    await updateSystemStatus('booking', {
      status: 'degraded',
      statusText: 'DEGRADED',
      description: `Booking system is experiencing Venpos API errors. Endpoint: ${endpoint}`,
      incident
    });
    console.log('✅ [MONITORING] System status updated to DEGRADED due to Venpos API error');
  } catch (statusError) {
    console.error('❌ [MONITORING] Failed to update system status:', statusError);
  }
  
  // Check if maintenance mode should be triggered
  if (shouldTriggerMaintenanceMode(CRITICAL_ERROR_TYPES.VENPOS_API_ERROR)) {
    await enableMaintenanceMode(`Multiple Venpos API errors detected. Endpoint: ${endpoint}`);
    
    // Send additional alert about maintenance mode
    await sendErrorNotification({
      errorType: 'MAINTENANCE_MODE_TRIGGERED',
      errorMessage: `Maintenance mode automatically enabled due to multiple Venpos API errors. Endpoint: ${endpoint}`,
      endpoint,
      context: {
        triggerReason: 'Multiple Venpos API errors',
        endpoint,
        threshold: CRITICAL_ERROR_THRESHOLD,
        timeWindow: '5 minutes'
      },
      timestamp: new Date().toISOString()
    });
  }
};

/**
 * Monitor basket creation failures
 */
export const monitorBasketCreationFailure = async (error, context = {}) => {
  // Sanitize error message - remove any "Test:" prefixes
  const rawErrorMessage = error?.message || String(error);
  const sanitizedErrorMessage = rawErrorMessage.replace(/^Test:\s*/i, '').trim();
  
  const errorDetails = {
    errorType: CRITICAL_ERROR_TYPES.BASKET_CREATION_FAILED,
    errorMessage: `Failed to create basket in Venpos. ${sanitizedErrorMessage}`,
    endpoint: 'Basket/Create or Basket/Load',
    error: sanitizedErrorMessage,
    context: {
      ...context,
      endpoint: 'Basket Creation',
      apiBase: 'Venpos Journey API',
      errorDetails: sanitizedErrorMessage
    },
    locationId: context?.locationId,
    timestamp: new Date().toISOString(),
    stackTrace: error?.stack
  };
  
  console.error('🚨 [MONITORING] Basket Creation Failed:', error);
  
  // Send notification
  await sendErrorNotification(errorDetails);
  
  // Automatically update system status to degraded when basket creation fails
  try {
    const incident = {
      title: 'Basket Creation Failed',
      description: `Failed to create basket in Venpos. ${sanitizedErrorMessage}`,
      timestamp: new Date().toLocaleString('en-GB', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }),
      updates: []
    };
    
    await updateSystemStatus('booking', {
      status: 'degraded',
      statusText: 'DEGRADED',
      description: 'Booking system is experiencing basket creation failures.',
      incident
    });
    console.log('✅ [MONITORING] System status updated to DEGRADED due to basket creation failure');
  } catch (statusError) {
    console.error('❌ [MONITORING] Failed to update system status:', statusError);
  }
  
  // Check if maintenance mode should be triggered
  if (shouldTriggerMaintenanceMode(CRITICAL_ERROR_TYPES.BASKET_CREATION_FAILED)) {
    await enableMaintenanceMode('Multiple basket creation failures detected');
    
    // Send additional alert about maintenance mode
    await sendErrorNotification({
      errorType: 'MAINTENANCE_MODE_TRIGGERED',
      errorMessage: `Maintenance mode automatically enabled due to multiple basket creation failures`,
      endpoint: 'Basket Creation',
      context: {
        triggerReason: 'Multiple basket creation failures',
        threshold: CRITICAL_ERROR_THRESHOLD,
        timeWindow: '5 minutes'
      },
      timestamp: new Date().toISOString()
    });
  }
};

/**
 * Monitor payment initialization failures
 */
export const monitorPaymentInitializationFailure = async (error, context = {}) => {
  // Sanitize error message - remove any "Test:" prefixes
  const rawErrorMessage = error?.message || String(error);
  const sanitizedErrorMessage = rawErrorMessage.replace(/^Test:\s*/i, '').trim();
  
  const errorDetails = {
    errorType: CRITICAL_ERROR_TYPES.PAYMENT_INITIALIZATION_FAILED,
    errorMessage: `Failed to initialize payment. ${sanitizedErrorMessage}`,
    endpoint: 'Payment/Initialize or POST /Payment',
    error: sanitizedErrorMessage,
    context: {
      ...context,
      endpoint: 'Payment Initialization',
      apiBase: 'Venpos Journey API',
      errorDetails: sanitizedErrorMessage
    },
    orderId: context?.basketId || context?.orderId,
    amount: context?.amount,
    timestamp: new Date().toISOString(),
    stackTrace: error?.stack
  };
  
  console.error('🚨 [MONITORING] Payment Initialization Failed:', error);
  
  // Send notification
  await sendErrorNotification(errorDetails);
  
  // Automatically update system status to degraded when payment initialization fails
  try {
    const incident = {
      title: 'Payment Initialization Failed',
      description: `Failed to initialize payment. ${sanitizedErrorMessage}`,
      timestamp: new Date().toLocaleString('en-GB', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }),
      updates: []
    };
    
    await updateSystemStatus('booking', {
      status: 'degraded',
      statusText: 'DEGRADED',
      description: 'Booking system is experiencing payment initialization failures.',
      incident
    });
    console.log('✅ [MONITORING] System status updated to DEGRADED due to payment initialization failure');
  } catch (statusError) {
    console.error('❌ [MONITORING] Failed to update system status:', statusError);
  }
  
  // Check if maintenance mode should be triggered
  if (shouldTriggerMaintenanceMode(CRITICAL_ERROR_TYPES.PAYMENT_INITIALIZATION_FAILED)) {
    await enableMaintenanceMode('Multiple payment initialization failures detected');
    
    // Send additional alert about maintenance mode
    await sendErrorNotification({
      errorType: 'MAINTENANCE_MODE_TRIGGERED',
      errorMessage: `Maintenance mode automatically enabled due to multiple payment initialization failures`,
      endpoint: 'Payment Initialization',
      context: {
        triggerReason: 'Multiple payment initialization failures',
        threshold: CRITICAL_ERROR_THRESHOLD,
        timeWindow: '5 minutes'
      },
      timestamp: new Date().toISOString()
    });
  }
};

/**
 * Reset maintenance mode (call this when issues are resolved)
 */
export const resetMaintenanceMode = async () => {
  try {
    // Always use live endpoint for maintenance mode (even when testing on localhost)
    const apiUrl = `${LIVE_API_ENDPOINT}/custom/v1/disable-maintenance-mode`;
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      },
      cache: 'no-store'
    });
    
    if (response.ok) {
      maintenanceModeActive = false;
      errorHistory = []; // Clear error history
      console.log('✅ [MONITORING] Maintenance mode disabled successfully');
      
      // Automatically update system status back to operational
      try {
        await updateSystemStatus('booking', {
          status: 'operational',
          statusText: 'OPERATIONAL',
          description: 'Booking system is fully operational. All issues have been resolved.',
          incident: null,
          maintenanceMode: {
            enabled: false,
            reason: '',
            timestamp: new Date().toISOString()
          }
        });
        console.log('✅ [MONITORING] System status updated to OPERATIONAL');
      } catch (statusError) {
        console.error('❌ [MONITORING] Failed to update system status:', statusError);
        // Don't throw - maintenance mode is still disabled
      }
    } else {
      const errorText = await response.text().catch(() => 'Unknown error');
      console.warn(`⚠️ [MONITORING] Failed to disable maintenance mode via API (${response.status}):`, errorText);
    }
  } catch (error) {
    // Handle network errors gracefully
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      console.warn('⚠️ [MONITORING] Network error disabling maintenance mode (API may be unreachable):', error.message);
    } else {
      console.error('❌ [MONITORING] Error disabling maintenance mode:', error);
    }
  }
};

/**
 * Get current maintenance mode status from API
 */
export const checkMaintenanceModeStatus = async () => {
  // Always use live endpoint for maintenance mode (even when testing on localhost)
  let apiUrl = `${LIVE_API_ENDPOINT}/custom/v1/check-maintenance-mode`;
  
  try {
    // Log the API URL being used (helpful for debugging)
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 [MONITORING] Checking maintenance mode at (LIVE):', apiUrl);
    }
    
    // Use cache: 'no-store' without custom headers to avoid CORS preflight issues
    // The cache option alone is sufficient to prevent caching
    const fetchOptions = {
      method: 'GET',
      cache: 'no-store' // Prevent caching
    };
    
    let response = await fetch(apiUrl, fetchOptions);
    
    // If 404, try alternative path (WordPress might be at root instead of /wp)
    if (response.status === 404 && LIVE_API_ENDPOINT.includes('/wp/wp-json')) {
      const altApiUrl = apiUrl.replace('/wp/wp-json', '/wp-json');
      console.warn('⚠️ [MONITORING] Primary path failed, trying alternative:', altApiUrl);
      response = await fetch(altApiUrl, fetchOptions);
      apiUrl = altApiUrl; // Use alternative for future calls
    }
    
    // Handle successful responses (200-299) and 304 (Not Modified)
    if (response.ok || response.status === 304) {
      // For 304, we might not have a body, so return default
      if (response.status === 304) {
        console.warn('⚠️ [MONITORING] Received 304 (Not Modified), using cached/default status');
        return { enabled: false, reason: '' };
      }
      
      try {
        const data = await response.json();
        return data;
      } catch (jsonError) {
        console.warn('⚠️ [MONITORING] Failed to parse JSON response:', jsonError);
        return { enabled: false, reason: '' };
      }
    }
    
    // For other status codes, log but don't throw
    console.warn(`⚠️ [MONITORING] Unexpected status code: ${response.status}`);
    return { enabled: false, reason: '' };
  } catch (error) {
    // Handle network errors gracefully
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      console.warn('⚠️ [MONITORING] Network error checking maintenance mode (this is expected if API is unreachable):', error.message);
    } else {
      console.error('❌ [MONITORING] Error checking maintenance mode:', error);
    }
    // Return default (not in maintenance mode) on error
    return { enabled: false, reason: '' };
  }
};

/**
 * Get local maintenance mode status
 */
export const getMaintenanceModeStatus = () => {
  return maintenanceModeActive;
};

